import type { HierarchyItem } from '@/shared/cors-provenance-api';
import { isMboxEmailJsonUrl, urlLooksLikeEml } from '@/shared/email-citation';
import { extractPathAfterBulk, getDispatchedFilePath } from '@/shared/functions';
import type { Source } from '@/shared/types';

function pathFileName(path: string): string | null {
  return path.split('?')[0].split('/').pop() || null;
}

function isMailboxContainerPath(path?: string): boolean {
  const normalized = path?.split(/[?#]/)[0]?.toLowerCase() ?? '';
  return normalized.endsWith('.mbox') || normalized.endsWith('.mbx');
}

function isEmailBodyArtifact(url?: string | null): boolean {
  if (!url?.trim()) return false;
  return urlLooksLikeEml(url) || isMboxEmailJsonUrl(url);
}

function contextDispatchedPath(source: Source): string {
  const raw =
    source.sourceContext?.dispatchedFilePath ?? source.sourceContext?.dispatched_file_path;
  return typeof raw === 'string' ? raw.trim() : '';
}

/**
 * Parent ``.eml`` for an mbox attachment citation. Top-level
 * ``dispatchedFilePath`` stays the attachment (so download / file-info target
 * one object); callers such as ``resolveEmailFetchArgs`` still need the email
 * extract or they fall back to ``/mbox`` without a reliable index.
 */
function parentEmailPathForAttachment(
  source: Source,
  dispatchedPath: string,
  subDocumentPath: string,
): string | undefined {
  const candidates = [dispatchedPath, contextDispatchedPath(source)];
  return candidates.find(
    (path) => !!path && isEmailBodyArtifact(path) && path !== subDocumentPath,
  );
}

/**
 * Resolve a container citation (zip / mbox / hierarchy) to the inner object
 * that toolbar actions should use. Keeps a distinct ``previewFilePath`` proxy
 * (e.g. ``.webp`` for a ``.heic``). For an mbox attachment, action paths point
 * at the attachment while context keeps the parent ``.eml``.
 */
function isZipContainerPath(path?: string): boolean {
  return (path?.split(/[?#]/)[0]?.toLowerCase() ?? '').endsWith('.zip');
}

export function resolveSourceForActions(
  source: Source,
  hierarchy?: HierarchyItem[] | null,
): Source {
  const incomingOriginal = (source.originalUrl || '').trim();
  const fallbackContainer = source.sourceFilePath || source.sourceUrl;
  const isMbox =
    isMailboxContainerPath(source.sourceFilePath) || isMailboxContainerPath(incomingOriginal);
  const dispatchedPath = getDispatchedFilePath(source);
  const analysisPath = source.analysisFilePath?.trim() || '';
  const hasParentEmail =
    isEmailBodyArtifact(dispatchedPath) || isEmailBodyArtifact(contextDispatchedPath(source));
  const analysisIsCitedAttachment =
    !!analysisPath &&
    analysisPath !== source.sourceFilePath &&
    !isEmailBodyArtifact(analysisPath) &&
    (isMbox || hasParentEmail);

  let subDocumentPath: string | null = null;
  let subDocumentFileName: string | null = null;

  const sourcePath = (source.sourceFilePath || '').trim();
  const sourceIsAlreadyAttachment =
    !!sourcePath &&
    !isMailboxContainerPath(sourcePath) &&
    !isZipContainerPath(sourcePath) &&
    !isEmailBodyArtifact(sourcePath) &&
    (isMbox || hasParentEmail);

  if (analysisIsCitedAttachment) {
    subDocumentPath = analysisPath;
    subDocumentFileName = pathFileName(analysisPath);
  } else if (sourceIsAlreadyAttachment) {
    subDocumentPath = sourcePath;
    subDocumentFileName = pathFileName(sourcePath);
  } else if (
    dispatchedPath &&
    dispatchedPath !== source.sourceFilePath &&
    !isMboxEmailJsonUrl(dispatchedPath)
  ) {
    subDocumentPath = dispatchedPath;
    subDocumentFileName = pathFileName(dispatchedPath);
  } else if (
    isMbox &&
    analysisPath &&
    analysisPath !== source.sourceFilePath &&
    !isMboxEmailJsonUrl(analysisPath)
  ) {
    subDocumentPath = analysisPath;
    subDocumentFileName = pathFileName(analysisPath);
  } else if (hierarchy && hierarchy.length > 1) {
    const deepestItem = hierarchy[hierarchy.length - 1];
    subDocumentPath = deepestItem.s3Url;
    subDocumentFileName = deepestItem.fileName || pathFileName(deepestItem.s3Url);
  }

  if (!subDocumentPath) return source;

  const keepIncomingOriginal =
    !!incomingOriginal &&
    incomingOriginal !== subDocumentPath &&
    (isMailboxContainerPath(incomingOriginal) || isZipContainerPath(incomingOriginal));
  const containerPath = keepIncomingOriginal ? incomingOriginal : fallbackContainer;

  const preview = source.previewFilePath?.trim() || '';
  const previewIsContainer = !preview || preview === containerPath || preview === source.sourceFilePath;
  const previewIsParentEmail =
    analysisIsCitedAttachment && !!dispatchedPath && preview === dispatchedPath && preview !== subDocumentPath;
  const previewFilePath = previewIsContainer || previewIsParentEmail ? undefined : source.previewFilePath;
  const label =
    subDocumentFileName || extractPathAfterBulk(subDocumentPath, true).truncatedFilePath || undefined;

  const parentEmailPath = parentEmailPathForAttachment(source, dispatchedPath, subDocumentPath);
  const sourceContext = {
    ...source.sourceContext,
    originalUrl: containerPath,
    dispatchedFilePath: parentEmailPath ?? subDocumentPath,
  };
  if (analysisIsCitedAttachment || sourceIsAlreadyAttachment) {
    sourceContext.attachmentUrl = analysisPath || sourcePath;
  } else if ('attachmentUrl' in sourceContext) {
    delete sourceContext.attachmentUrl;
  }

  return {
    ...source,
    originalUrl: containerPath,
    dispatchedFilePath: subDocumentPath,
    sourceFilePath: subDocumentPath,
    sourceUrl: subDocumentPath,
    previewFilePath,
    label,
    truncatedFilePath: label,
    location: subDocumentPath,
    sourceContext,
  };
}
